
<template>  
  <div class="cart">
    <div class="cart__wrapper container">
      <div class="cart__container">
        <div class="cart__heading-container">
          <h2 class="h5 cart__title">Your Cart {{cartCount(totalItems)}}</h2>
        </div>
        <transition name="fade">
          <div class="cart__contents-wrapper">
            <div class="cart__shipping-promo--main-wrapper" v-if="shippingPromoEnabled">
              <div class="cart__shipping-promo--wrapper desktop">
                <div class="cart__shipping-promo">
                  <transition name="fade">
                    <span class="cart__shipping-promo-message body-md" v-if="normalizedTotalPrice < shippingPromoTotal" :class="colors.shippingText">
                      {{`You are ${currency}${remainingThresholdValue} away from free shipping.`}}
                    </span>
                    <span class="cart__shipping-promo-message body-md" v-if="normalizedTotalPrice >= shippingPromoTotal" :class="colors.shippingText">
                      {{shippingPromoCopy}}
                    </span>
                  </transition>
                  <div class="cart__shipping-promo-progress-bar-desktop">
                    <transition name="fade">
                      <div class="cart__shipping-promo-progress-bar" :style="{width: `${remainingThresholdPercentage}%`}" :class="colors.shippingProgress"></div>
                    </transition>
                  </div>
                </div>
              </div>
            </div>
            <div class="cart__contents">
              <transition-group name="fade" mode="out-in">
                <template v-if="totalItems > 0">
                  <sidecart-line-item class="in-cart" v-for="(item, index) in lineItems" :item="item" :key="`in-cart_${index}`"></sidecart-line-item>
                </template>
                <div key="div1-cart-empty" class="cart__empty" v-else>
                  <p class="cart__empty__message">{{ emptyText }}</p>
                  <div class="cart__empty__link-container" key="div">
                    <v-button class="cart__empty__link" v-for="(link, index) in emptyLinkArray" :primary="index !== 1" :secondary="index === 1" :href="link.url" :label="link.text" :key="index" tag="a" v-if="link.text"></v-button>
                  </div>
                </div>
              </transition-group>
            </div>
          </div>
        </transition>
      </div>
      <div class="cart__sidebar">
        <div class="cart__total-checkout">
          <table class="cart__table">
            <tbody class="cart__table-body">
              <tr class="cart__table-row">
                <td class="cart__table-data-type subtotal strong-bold">Subtotal</td>
                <td class="cart__table-data-price subtotal">{{ totalPrice | money }}</td>
              </tr>
              <tr class="cart__table-row color-util-sale" v-if="savingsTotal && savingsTotal > 0">
                <td class="cart__table-data-type savings strong-bold">You Saved</td>
                <td class="cart__table-data-price savings">{{ savingsTotal | money }}</td>
              </tr>
              <tr class="cart__table-row cart__table-row--footnote">
                <td class="cart__table-data-footnote body-sm">*Shipping Calculated at Checkout</td>
              </tr>
            </tbody>
          </table>
          
        </div>
        <v-button class="cart__checkout-cta" :disabled="totalItems === 0" :full="true" @click="goToCheckout" type="submit" name="checkout" value="Checkout" :class="[{'cart__checkout-cta-promo-active' : promoMessage }, colors.cta]" label="Checkout"></v-button>
        <div class="cart__promo" v-if="promoMessage">
          <p class="cart__promo__copy body-sm">{{ promoMessage }}</p>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { mapState } from 'vuex'

import Icon from '@/scripts/components/basic/Icon.vue'
import VButton from '@/scripts/components/buttons/VButton.vue'
import VLink from '@/scripts/components/buttons/VLink.vue'
import AsyncButton from '@/scripts/components/buttons/AsyncButton.vue'
import TextAreaInput from '@/scripts/components/forms/TextAreaInput.vue'
import CartLineItem from '@/scripts/components/cart/CartLineItem.vue'
import SidecartLineItem from '@/scripts/components/cart/SidecartLineItem.vue'
import axios from 'axios'

export default {
  name: 'Cart',
  components: { VButton, CartLineItem, SidecartLineItem, Icon, VLink, TextAreaInput, AsyncButton },
  props: {
    cartObject: Object,
    promoMessage: String,
    emptyText: String,
    emptyLinkArray: Array,
    wishlistCta: Object,
    shippingPromoEnabled: Boolean,
    shippingPromoTotal: Number,
    shippingPromoCopy: String,
    continueShoppingLink: String,
    colors: {
      type: Object,
      default: () => window.bvaccel.cart.colors
    }
  },
  data () {
    return {
      lineItemsBottom: false,
      currency: window.bvaccel.currency
    }
  },
  computed: {
    ...mapState({
      allProducts: state => state.products.all,
      sidecartOpen: state => state.cart.sidecartOpen,
      items: state => state.cart.checkout.items,
      itemsSubtotal: state => state.cart.checkout.items_subtotal_price,
      totalDiscount: state => state.cart.checkout.total_discount,
      totalPrice: state => state.cart.checkout.total_price,
      totalItems: state => state.cart.checkout.item_count
    }),
    normalizedTotalPrice () {
      return parseFloat(this.totalPrice / 100)
    },
    remainingThresholdValue () {
      if (this.normalizedTotalPrice < this.shippingPromoTotal) {
        return parseFloat((this.shippingPromoTotal - this.normalizedTotalPrice)).toFixed(2)
      }
      return 0
    },
    remainingThresholdPercentage () {
      if (this.normalizedTotalPrice < this.shippingPromoTotal) {
        return parseInt((this.normalizedTotalPrice / this.shippingPromoTotal * 100).toFixed(0))
      }
      return 100
    },
    savingsTotal () {
      let totalFullPrices = 0
      let totalDiscounts = 0
      const fullPrices = []
      const discountedPrices = []
      this.items && this.items.forEach(item => {
        this.allProducts.forEach(product => {
          if (item.id === product.id && product.compare_at_price !== item.price) {
            fullPrices.push(product.compare_at_price * item.quantity)
          }
          product.variants.forEach(variant => {
            if (item.id === variant.id && variant.compare_at_price !== variant.price) {
              fullPrices.push(variant.compare_at_price * item.quantity)
            }
          })
        })
      })
      this.items && this.items.forEach(item => {
        this.allProducts.forEach(product => {
          if (item.id === product.id && product.compare_at_price !== item.price) {
            discountedPrices.push(product.price * item.quantity)
          }
          product.variants.forEach(variant => {
            if (item.id === variant.id && variant.compare_at_price !== variant.price) {
              discountedPrices.push(variant.price * item.quantity)
            }
          })
        })
      })

      discountedPrices.forEach(price => totalDiscounts += price)
      fullPrices.forEach(price => totalFullPrices += price)

      return totalFullPrices - totalDiscounts
    },
    pageTopHeight () {
      const navHeight = document.querySelector('.main-navigation-container').offsetHeight
      const headingHeight = 48
      let promoHeight
      if (document.querySelector('.cart__shipping-promo--wrapper.mobile')) {
        promoHeight = document.querySelector('.cart__shipping-promo--wrapper.mobile').offsetHeight
      } else {
        promoHeight = 0
      }
      return navHeight + headingHeight + promoHeight
    },
    lineItems () {
      return this.items;
    }
  },
  mounted () {
    if (this.thresholdProductHandle) {
      this.$store.dispatch('products/addProduct', this.thresholdProductHandle)
    }
    this.initBoldUpsell()
  },
  updated () {
    this.initBoldUpsell()
  },
  methods: {
    initBoldUpsell () {
      // init bold
      if (window.BOLD && BOLD.common && BOLD.common.eventEmitter && typeof BOLD.common.eventEmitter.emit === 'function') {
        BOLD.common.eventEmitter.emit('BOLD_COMMON_cart_loaded')
      }
    },
    checkSpecialChars (message) {
      const pattern = RegExp(/[^\u0020-\u007E]/gm)
      return pattern.test(message)
    },
    goToCheckout () {
      window.location = '/checkout'
    },
    cartCount (totalItems) {
      if (totalItems < 100 && totalItems !== 0) {
        return `(${totalItems})`
      } else if (totalItems > 99) {
        return '(99+)'
      } else if (totalItems === 0) {
        return
      }
    }
  }
}
</script>

<style scoped lang="scss">
  .cart {
    display: flex;
    flex-direction: column;
    background-color: $color-white;
    padding: 40px 0;
    @include mobile-only {
      padding: 0;
    }
    &__title {
      text-align: left;
      margin-bottom: 20px;
    }
    &__wrapper {
      display: flex;
      justify-content: space-between;
      padding-bottom: 50px;
      width: 100%;
      padding-top: 24px;
      &.container {
        max-width: $container-max-width-slim;
      }

      @include tablet-up {
        padding-top: 40px;
      }

      @include mobile-only {
        flex-direction: column;
      }
    }

    &__container {
      width: 100%;
    }

    &__contents-wrapper {
      display: flex;
      flex-grow: 1;
      width: 100%;
      &-mobile {
        height: 100%;
        position: relative;
      }
    }
    &__sidebar {
      width: 100%;
      max-width: 416px;
      @include mobile-only {
        width: calc(100% + 30px);
        position: relative;
        left: -15px;
        margin-top: 20px;
        background-color: $color-brand-tertiary;
        padding: 0 15px;
        max-width: calc(100% + 30px);
      }
    }

    &__contents-table-header {
      &--product {
        width: 60%;
      }
      &--quantity {
        width: 20%;
      }
      &--price {
        width: 20%;
      }
    }

    &__heading-container {
      width: 100%;
      display: flex;
      flex-direction: column;
      justify-content: flex-start;
      align-items: flex-start;

      > * + * {
        margin-top: 8px;
      }

      h1 {
        display: block;
        font-family: $font-family-secondary;
      }
      span {
        font-family: $font-family-primary;
      }
    }
    &__shipping-promo.container {
      padding: 9px $grid-gutter;
    }
    &__shipping-promo {
      &--main-wrapper {
        padding-right: 36px;
        @include mobile-only {
          padding-right: 0;
        }
      }
      @include desktop-up {
        > * {
          display: block;
        }
      }
      &-continue-shopping {
        width: auto;
      }
    }
    &__shipping-promo-progress-bar-desktop {
      background-color: $color-brand-tertiary;
      width: 100%;
      height: 3px;
      margin: 20px 0;
    }

    &__shipping-promo-progress-bar {
      height: 3px;
      transition: width 200ms ease;
      margin-bottom: 50px;

      @include mobile-tablet {
        margin-bottom: 24px;
      }
      @include desktop-up {
        margin: 0;
      }
    }
    &__contents {
      flex-grow: 1;
      padding: 0;
      overflow: visible; // allow quantity select dropdown to overflow
      margin-right: $grid-gutter;
      border-top: 1px solid $color-lightest-grey;
      border-bottom: 1px solid $color-lightest-grey;

      @include mobile-only {
        border-top: 0;
        margin-right: 0;
      }

      &-wrapper {
        display: flex;
        flex-direction: column;
        justify-content: space-between;
        flex-grow: 1;
      }
    }
    &__footer {
      padding-top: 8px;
      border: none;
      @include mobile-tablet {
        display: flex;
        flex-direction: column;
        padding: 16px;
        width: 100%;
        background-color: $color-white;
        align-content: flex-end;
      }
    }
    &__total-checkout {
      display: flex;
      flex-direction: column;
      padding: 20px;
      background: $color-brand-tertiary;
      margin-bottom: 15px;
      box-shadow: none;

      @include mobile-only {
        padding: 15px 0;
        box-shadow: none;
      }
    }
    &__table {
      display: table;
      width: 100%;
      &-body {
        width: 100%;
      }
      &-row {
        display: table;
        width: 100%;
        &--footnote {
          margin-top: 10px;
        }
      }
      &-data-price {
        text-align: right;
      }
      &-data-footnote {
        color: $color-grey;
      }
    }
    &__promo {
      background-color: $color-brand-tertiary;
      padding: 20px $grid-gutter;

      @include desktop-up {
        margin-top: 16px;
      }

      @include mobile-only {
        padding: 20px 15px;
      }
      
      &__copy {
        margin: 0;
        padding: 0;
        text-align: center;
      }
    }
    &__empty {
      display: flex;
      flex-direction: column;
      justify-content: center;
      align-items: center;
      text-align: center;
      width: 100%;
      padding: 80px 20px;

      &__link-container {
        display: flex;
        align-items: center;
        @include mobile-only {
          flex-direction: column;
        }
      }

      /deep/ .cart__empty__link {
        margin-bottom: 0;
        width: 120px; 
        &:first-child {
          margin-right: 15px;
        }

        @include mobile-only {
          margin-bottom: 15px;
          &:first-child {
            margin-right: 0;
          }
        }
      }

      &__message {
        @include h6-desktop;
      }
      &__image,
      &__message {
        margin: 0 0 24px;
      }
      &__link {
        width: 220px;
        margin-bottom: 16px;
        padding: 0;
      }
    }
    &__checkout-cta {
      height: 50px;
    }
    &__continue-shopping {
      padding-top: 16px;
      padding-bottom: 4px;
      text-align: center;
      width: 100%;
    }
    &__continue-shopping-link-container {
      display: inline-block;
      margin: 0 auto;
    }
  }

  .cart__checkout-cta.link--secondary {
    &:hover {
      &:before {
        height: 100%;
      }
    }
  }

  .cart-type-bar {
    padding-top: 28px;
    padding-bottom: 24px;
    width: 100%;

    @include mobile-only {
      justify-content: center;
    }

    &__tab-wrap {
      display: flex;
      width: 100%;

      @include tablet-up {
        width: 75%;
      }
    }

    &__tab {
      width: 170px;
      height: 40px;
      background: $color-white;
      border: 1px solid $color-black;
      border-radius: $border-radius--light;

      @include mobile-only {
        width: 50%;
      }
    }

    &__tab:disabled {
      background: $color-light-grey;
      border: none;
    }
  }
</style>
